import { r, s } from '@/theme/tokens';
import {
  buildFilterAutocompleteListboxSx,
  filterDropdownMaxWidth,
  filterInputSx,
  mergeFilterSlotSx,
} from './filterControlStyles';
import type { SxProps, Theme } from '@mui/material/styles';

export const selectFilterDefaultControlWidthPx = 180;
export const selectFilterDefaultFontSizePx = 13;
export const selectFilterPopperOverflowPaddingPx = 16;
export const selectFilterReadOnlyInputStyle = { cursor: 'pointer' } as const;

type SxObject = Record<string, unknown>;

const isSxObject = (value: unknown): value is SxObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const resolveSxObject = (sx: SxProps<Theme>, theme: Theme): SxObject => {
  if (!sx) return {};
  const resolved: unknown = typeof sx === 'function' ? sx(theme) : sx;
  if (Array.isArray(resolved)) {
    return resolved.reduce<SxObject>((acc, item) => {
      if (isSxObject(item)) {
        return { ...acc, ...item };
      }
      return acc;
    }, {});
  }
  if (isSxObject(resolved)) {
    return resolved;
  }
  return {};
};

type BuildSelectFilterRootSxArgs = {
  fullWidth: boolean;
  hasLabel: boolean;
  labelControlGap: string;
  labelPlacement?: 'stacked';
};

export const buildSelectFilterRootSx = ({
  fullWidth,
  hasLabel,
  labelControlGap,
  labelPlacement: _labelPlacement,
}: BuildSelectFilterRootSxArgs): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: hasLabel ? labelControlGap : s('none'),
  minWidth: 0,
  width: fullWidth ? '100%' : 'auto',
});

export const buildSelectFilterLabelSx =
  (_fontSizePx: number, _labelPlacement: 'stacked' = 'stacked'): SxProps<Theme> =>
  (theme) => ({
    whiteSpace: 'normal',
    fontSize: theme.typography.subtitle1.fontSize ?? theme.typography.body1.fontSize,
    lineHeight: theme.typography.subtitle1.lineHeight ?? theme.typography.body1.lineHeight,
    fontWeight: theme.typography.fontWeightRegular,
    letterSpacing: 0,
  });

export const buildSelectFilterLabelWidthSx = (
  labelWidthPx: number | undefined,
): SxProps<Theme> | undefined =>
  labelWidthPx === undefined
    ? undefined
    : {
        display: 'inline-flex',
        justifyContent: 'flex-end',
        width: `${labelWidthPx}px`,
      };

type BuildSelectFilterControlFrameSxArgs = {
  fullWidth: boolean;
  labelPlacement?: 'stacked';
  resolvedControlWidth: string;
};

export const buildSelectFilterControlFrameSx = ({
  fullWidth,
  labelPlacement: _labelPlacement,
  resolvedControlWidth,
}: BuildSelectFilterControlFrameSxArgs): SxProps<Theme> => ({
  width: fullWidth ? '100%' : resolvedControlWidth,
  minWidth: fullWidth ? 0 : resolvedControlWidth,
  maxWidth: fullWidth ? 'none' : resolvedControlWidth,
  flex: undefined,
});

export const multiSelectFilterSelectedValueSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  pl: s('sm'),
} as const;

export const buildMultiSelectFilterSelectedSummarySx = (fontSizePx: number): SxProps<Theme> => ({
  fontSize: `${fontSizePx}px`,
  lineHeight: 1.4,
});

type BuildSelectFilterOptionRowSxArgs = {
  optionRowMinHeight: string;
};

export const buildSelectFilterOptionRowSx = ({
  optionRowMinHeight,
}: BuildSelectFilterOptionRowSxArgs): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  px: s('md'),
  py: s('sm'),
  minHeight: optionRowMinHeight,
  minWidth: 0,
});

export const multiSelectFilterOptionCheckboxSx = {
  p: s('none'),
} as const;

export const buildSelectFilterOptionLabelSx =
  (fontSizePx: number): SxProps<Theme> =>
  (theme) => ({
    fontSize: `${fontSizePx}px`,
    lineHeight: 1.4,
    fontWeight: theme.typography.fontWeightLight,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

export const buildSelectFilterOptionCountSx =
  (countFontSizePx: number): SxProps<Theme> =>
  (theme) => ({
    ml: 'auto',
    pl: s('md'),
    flex: '0 0 auto',
    fontSize: `${countFontSizePx}px`,
    lineHeight: 1.3,
    fontWeight: theme.typography.fontWeightRegular,
    fontVariantNumeric: 'tabular-nums',
    opacity: 0.82,
  });

type BuildMultiSelectFilterAutocompleteSxArgs = {
  controlHeight: number;
  fontSizePx: number;
  hasActiveValue: boolean;
  inputBackgroundColor: string;
};

type BuildSingleSelectFilterAutocompleteSxArgs = BuildMultiSelectFilterAutocompleteSxArgs & {
  inputLeftPadding: string;
};

export const buildMultiSelectFilterAutocompleteSx =
  ({
    controlHeight,
    fontSizePx,
    hasActiveValue,
    inputBackgroundColor,
  }: BuildMultiSelectFilterAutocompleteSxArgs): SxProps<Theme> =>
  (theme) => ({
    ...resolveSxObject(filterInputSx, theme),
    width: '100%',
    minWidth: 0,
    '& .MuiAutocomplete-inputRoot': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      maxHeight: `${controlHeight}px !important`,
      backgroundColor: inputBackgroundColor,
      alignItems: 'center',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      minWidth: 0,
      cursor: 'pointer',
    },
    '& .MuiAutocomplete-input': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      marginLeft: `${s('none')} !important`,
      lineHeight: '1 !important',
      cursor: 'pointer',
    },
    '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
      ...theme.typography.body1,
      fontSize: `${fontSizePx}px`,
      cursor: 'pointer',
      color: theme.app.colors.text,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasActiveValue ? theme.app.colors.primary : undefined,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        borderColor: theme.app.colors.primary,
      },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.app.colors.primary,
      borderWidth: '2px',
    },
    '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
      ...theme.typography.body1,
      fontSize: `${fontSizePx}px`,
      cursor: 'pointer',
      color: theme.app.colors.text,
    },
    '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
      {
        ...theme.typography.body1,
        fontSize: `${fontSizePx}px`,
        color: theme.app.colors.textMuted,
        opacity: 1,
      },
    '& .MuiAutocomplete-tag': {
      margin: 0,
      maxWidth: '100%',
    },
    '& .Mui-disabled': {
      opacity: 1,
    },
    '& .MuiInputBase-input.Mui-disabled, & .MuiAutocomplete-input.Mui-disabled, & input:disabled': {
      WebkitTextFillColor: theme.palette.text.disabled,
      color: theme.palette.text.disabled,
    },
    '& .MuiSvgIcon-root.Mui-disabled, & .MuiAutocomplete-popupIndicator.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  });

export const buildSingleSelectFilterAutocompleteSx =
  ({
    controlHeight,
    fontSizePx,
    hasActiveValue,
    inputBackgroundColor,
    inputLeftPadding,
  }: BuildSingleSelectFilterAutocompleteSxArgs): SxProps<Theme> =>
  (theme) => {
    const baseSx = resolveSxObject(
      buildMultiSelectFilterAutocompleteSx({
        controlHeight,
        fontSizePx,
        hasActiveValue,
        inputBackgroundColor,
      }),
      theme,
    );
    const inputSx = isSxObject(baseSx['& .MuiAutocomplete-input'])
      ? baseSx['& .MuiAutocomplete-input']
      : {};
    const { '& .MuiAutocomplete-tag': _autocompleteTag, ...singleSelectSx } = baseSx;
    return {
      ...singleSelectSx,
      '& .MuiAutocomplete-input': {
        ...inputSx,
        paddingLeft: `${inputLeftPadding} !important`,
      },
    };
  };

type BuildSelectFilterPopperSxArgs = {
  dropdownWidth: string;
  fullWidth: boolean;
};

type SelectFilterPopperModifier = {
  name: string;
  enabled?: boolean;
  options?: {
    altAxis?: boolean;
    mainAxis?: boolean;
    offset?: [number, number];
    padding?: number;
  };
};

export const buildSelectFilterPopperModifiers = ({
  preventOverflow = false,
}: {
  preventOverflow?: boolean;
} = {}): SelectFilterPopperModifier[] => [
  {
    name: 'offset',
    options: {
      offset: [0, 0],
    },
  },
  ...(preventOverflow
    ? [
        {
          name: 'preventOverflow',
          options: {
            padding: selectFilterPopperOverflowPaddingPx,
            mainAxis: false,
            altAxis: true,
          },
        },
      ]
    : []),
];

export const buildMultiSelectFilterPopperOptions = (): {
  modifiers: SelectFilterPopperModifier[];
} => ({
  modifiers: [
    {
      name: 'flip',
      enabled: false,
    },
    {
      name: 'preventOverflow',
      options: {
        padding: selectFilterPopperOverflowPaddingPx,
        mainAxis: false,
        altAxis: true,
      },
    },
  ],
});

export const buildSelectFilterPopperSx = ({
  dropdownWidth,
  fullWidth,
}: BuildSelectFilterPopperSxArgs): SxProps<Theme> => ({
  '& .MuiAutocomplete-paper': {
    minWidth: dropdownWidth,
    width: fullWidth ? dropdownWidth : 'max-content',
    maxWidth: filterDropdownMaxWidth,
  },
});

type BuildSelectFilterListboxSxArgs = {
  dropdownWidth: string;
  optionRowMinHeight: string;
};

export const buildMultiSelectFilterListboxSx = ({
  dropdownWidth,
  optionRowMinHeight,
}: BuildSelectFilterListboxSxArgs): SxProps<Theme> =>
  mergeFilterSlotSx(buildFilterAutocompleteListboxSx(optionRowMinHeight), {
    p: s('xxs'),
    m: s('none'),
    minWidth: dropdownWidth,
    borderRadius: `${r('md')} !important`,
    '& .MuiAutocomplete-option': {
      whiteSpace: 'nowrap',
      borderRadius: `${r('xs')} !important`,
    },
    '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: (theme: Theme) => `${theme.app.state.hover} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: (theme: Theme) => `${theme.app.state.active} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
      {
        backgroundColor: (theme: Theme) => `${theme.app.state.active} !important`,
      },
  });

export const buildSingleSelectFilterListboxSx = ({
  dropdownWidth,
  optionRowMinHeight,
}: BuildSelectFilterListboxSxArgs): SxProps<Theme> =>
  mergeFilterSlotSx(buildFilterAutocompleteListboxSx(optionRowMinHeight), {
    p: s('none'),
    m: s('none'),
    minWidth: dropdownWidth,
    '& .MuiAutocomplete-option': {
      borderRadius: `${r('xs')} !important`,
      paddingLeft: `${s('md')} !important`,
      paddingRight: `${s('md')} !important`,
      whiteSpace: 'nowrap',
    },
    '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: (theme: Theme) => `${theme.app.state.hover} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: (theme: Theme) => `${theme.app.state.active} !important`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
      {
        backgroundColor: (theme: Theme) => `${theme.app.state.active} !important`,
      },
  });
